/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * CourseField
 * -----------
 * The course of a route-based activity: drawn on the map, or uploaded as GPX.
 *
 * Both ways in end in the same place:
 *
 *  • `route.route_polyline` — the line the public event page draws (GeoJSON).
 *  • a GPX file queued in the form's pending uploads, sent to the file server on
 *    save and written to `route.gpx_file_url` — what participants load onto a
 *    watch. A drawn course gets a GPX generated from the drawing; an uploaded
 *    one keeps the organizer's own file, which may be richer (timestamps,
 *    elevation) than the simplified line.
 *  • start and finish points, their addresses when the point has moved, and the
 *    total distance in whichever unit the organizer chose — all still editable
 *    in the fields below.
 *
 * Nothing leaves the browser until the event is saved, so abandoning the form
 * leaves no stray files on the server.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import FileUploadInput from '../../common/FileUploadInput';
import RouteDrawer from '../routes/RouteDrawer';
import CourseEditorModal from '../routes/CourseEditorModal';
import { activityFileField } from '../../athlohive/pendingUploads';
import { reverseGeocode } from '../../athlohive/geocode';
import readGpxFile from '../../athlohive/gpx/readGpxFile';
import {
  buildGpx, downloadGpx, gpxFile, gpxFileName,
} from '../../athlohive/gpx/buildGpx';
import { fromStoredLine, toLineString } from '../../athlohive/gpx/routeLine';
import {
  formatDistanceValue, haversineKm, kmToUnit, totalDistanceKm,
} from '../../athlohive/gpx/routeMath';

/** A start or finish that moved less than this keeps the address already typed. */
const ADDRESS_KEEP_KM = 0.025;
/** Nominatim's public server asks for at most one request a second. */
const GEOCODE_GAP_MS = 1100;

const hasPoint = (point) => point && point.lat !== '' && point.lng !== ''
  && Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng));

// const asPoint = (p) => ({ lat: String(Number(p.lat.toFixed(6))), lng: String(Number(p.lng.toFixed(6))) });
// Exactly the course's first/last coordinate — the same numbers as the map line
// and the GPX file, never re-rounded here.
const asPoint = (p) => ({ lat: String(Number(p.lat)), lng: String(Number(p.lng)) });

/** The route fields a course writes, so an abandoned upload can put them all back. */
const COURSE_FIELDS = [
  'route_polyline', 'start_point', 'start_address', 'end_point', 'end_address',
  'total_distance', 'distance_measuring_unit_id',
];

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * When the next address lookup may go out, shared by every course on the page.
 *
 * Spacing lookups only within one course was not enough: picking a file,
 * discarding it and picking another fires lookups back to back, the public
 * Nominatim server refuses the one that comes too soon, and that address was
 * silently left empty. So every lookup waits for its slot, and a refused one is
 * tried once more a slot later.
 */
let nextLookupAt = 0;
async function politeReverseGeocode(lat, lng, isStale) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const delay = nextLookupAt - Date.now();
    nextLookupAt = Math.max(Date.now(), nextLookupAt) + GEOCODE_GAP_MS;
    if (delay > 0) await wait(delay); // eslint-disable-line no-await-in-loop
    if (isStale()) return null;
    const found = await reverseGeocode(lat, lng); // eslint-disable-line no-await-in-loop
    if (found?.label || isStale()) return found;
  }
  return null;
}

/** Test seam: forget the shared lookup slot. */
export function resetLookupSlotForTests() { nextLookupAt = 0; }

/** Download link for a course, built from the same exact points the map shows. */
function GpxDownloadLink({ points, name }) {
  const [href, setHref] = useState(null);
  const key = JSON.stringify(points);
  useEffect(() => {
    const url = URL.createObjectURL(new Blob([buildGpx(points, name)], { type: 'application/gpx+xml' }));
    setHref(url);
    return () => URL.revokeObjectURL(url);
    // `key` stands in for `points`, which is a new array on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, name]);
  if (!href) return null;
  return (
    <a className="org-btn ghost orgr-linkbtn" href={href} download={gpxFileName(name)}>
      <i className="bi bi-download" aria-hidden="true" /> Download GPX
    </a>
  );
}

export default function CourseField({
  route, index, activityName, dispatch, unitOptions = [],
  pendingUploads = {}, setPendingFile, readOnly = false,
}) {
  const field = activityFileField(index, 'route.gpx_file_url');
  const pendingFile = pendingUploads[field] || null;
  const points = fromStoredLine(route.route_polyline);
  const hasCourse = points.length >= 2;
  const courseName = activityName || `Activity ${index + 1}`;

  const [mode, setMode] = useState(
    !hasCourse && (route.gpx_file_url || pendingFile) ? 'upload' : 'draw',
  );
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState(null); // { tone, text }
  const [fitKey, setFitKey] = useState(0);
  // Latest lookup wins: a second course applied while the first one's addresses
  // are still being fetched must not have its addresses overwritten by them.
  const lookupRef = useRef(0);

  useEffect(() => () => { lookupRef.current += 1; }, []);

  // Stable, because the modal re-binds Escape and body scroll whenever it changes.
  const closeEditor = useCallback(() => setEditing(false), []);

  const set = (name, value) => dispatch({
    type: 'updateActivityNested', index, group: 'route', field: name, value,
  });

  const fillAddresses = async (start, finish) => {
    const ticket = lookupRef.current + 1;
    lookupRef.current = ticket;
    const jobs = [];
    if (start) jobs.push(['start_address', start]);
    if (finish) jobs.push(['end_address', finish]);
    const stale = () => lookupRef.current !== ticket;
    for (let i = 0; i < jobs.length; i += 1) {
      // if (i > 0) await wait(GEOCODE_GAP_MS);
      if (stale()) return;
      const [name, p] = jobs[i];
      // const found = await reverseGeocode(p.lat, p.lng);
      const found = await politeReverseGeocode(p.lat, p.lng, stale); // eslint-disable-line no-await-in-loop
      if (stale()) return;
      // A failed lookup keeps whatever address is there — same rule as the pin picker.
      if (found?.label) set(name, found.label);
    }
  };

  /** Writes a course onto the activity. `file` is the GPX to attach, or undefined to leave it. */
  const applyCourse = (next, { file } = {}) => {
    if (next.length < 2) {
      set('route_polyline', null);
      if (file !== undefined) setPendingFile(field, null);
      return;
    }

    set('route_polyline', toLineString(next));

    const start = next[0];
    const finish = next[next.length - 1];
    const moved = (current, p) => !hasPoint(current)
      || haversineKm({ lat: Number(current.lat), lng: Number(current.lng) }, p) > ADDRESS_KEEP_KM;
    const startMoved = moved(route.start_point, start);
    const finishMoved = moved(route.end_point, finish);
    set('start_point', asPoint(start));
    set('end_point', asPoint(finish));
    const needStart = startMoved || !route.start_address;
    const needFinish = finishMoved || !route.end_address;
    if (needStart || needFinish) fillAddresses(needStart && start, needFinish && finish);

    // Distance in the organizer's chosen unit; Kilometre when none is chosen yet.
    const km = totalDistanceKm(next);
    let unit = unitOptions.find((option) => String(option.value) === String(route.distance_measuring_unit_id));
    if (!unit) {
      unit = unitOptions.find((option) => /kilomet/i.test(String(option.label)));
      if (unit) set('distance_measuring_unit_id', unit.value);
    }
    const inUnit = unit ? kmToUnit(km, unit.label) : null;
    if (inUnit !== null) set('total_distance', formatDistanceValue(inUnit, unit.label));

    if (file !== undefined) setPendingFile(field, file);
  };

  const onDrawn = (next) => {
    setEditing(false);
    setFitKey((key) => key + 1);
    if (next.length < 2) {
      applyCourse([], { file: null });
      setMessage(null);
      return;
    }
    applyCourse(next, { file: gpxFile(next, courseName) });
    setMessage({ tone: 'ok', text: 'Course set. Start, finish and distance below were filled in from it. Check them.' });
  };

  // Every course field as it was before a file was picked, so clearing that pick
  // puts the whole course back — line, start, finish and distance — instead of
  // leaving the discarded file's numbers in the form. `undefined` = nothing held.
  const beforeUploadRef = useRef(undefined);

  const onUploaded = async (file) => {
    if (!file) {
      // A just-picked file was cleared: back to the course from before the pick.
      const before = beforeUploadRef.current;
      if (before !== undefined) {
        lookupRef.current += 1; // drop any address lookup still in flight for the discarded file
        COURSE_FIELDS.forEach((name) => set(name, before[name]));
      }
      beforeUploadRef.current = undefined;
      setPendingFile(field, null);
      setMessage(null);
      return;
    }
    const result = await readGpxFile(file);
    if (!result) {
      setPendingFile(field, null);
      setMessage({
        tone: 'err',
        text: `${file.name} has no track, route or waypoints to read. Export it again as GPX, or draw the course instead.`,
      });
      return;
    }
    if (beforeUploadRef.current === undefined) {
      beforeUploadRef.current = COURSE_FIELDS.reduce((snapshot, name) => ({
        ...snapshot,
        [name]: name === 'route_polyline' ? (route[name] ?? null) : route[name],
      }), {});
    }
    setFitKey((key) => key + 1);
    applyCourse(result.points, { file });
    setMessage({
      tone: 'ok',
      text: `Read ${file.name}: a ${totalDistanceKm(result.points).toFixed(2)} km course. Start, finish `
        + 'and distance below were filled in from it. Open "Draw on map" to see or adjust it.',
    });
  };

  /** The already-saved file was cleared in the uploader: the course goes with it. */
  const onSavedFileChange = (value) => {
    set('gpx_file_url', value);
    if (!value) {
      set('route_polyline', null);
      setMessage(null);
    }
  };

  const onRemove = () => {
    lookupRef.current += 1;
    set('route_polyline', null);
    set('gpx_file_url', '');
    setPendingFile(field, null);
    setFitKey((key) => key + 1);
    setMessage(null);
  };

  // const attached = pendingFile
  //   ? { name: pendingFile.name, note: 'uploaded when you save the event' }
  //   : (route.gpx_file_url ? { name: route.gpx_file_url.split('/').pop(), note: 'already saved' } : null);
  // A stored file's URL ends in a storage id, not a name, and a link typed into
  // the box below has not been saved yet — so neither is labelled "already saved".
  const linkedName = (url) => {
    const last = String(url).split(/[?#]/)[0].split('/').pop() || '';
    return /\.gpx$/i.test(last) ? last : 'Course GPX file';
  };
  const attached = pendingFile
    ? { name: pendingFile.name, note: 'uploaded when you save the event' }
    : (route.gpx_file_url ? { name: linkedName(route.gpx_file_url), note: 'attached' } : null);

  return (
    <div className="orgf-field orgr-course">
      <span className="orgr-course__label" id={`orgr-course-${index}`}>Course</span>

      {!readOnly && (
        <div className="orgr-seg" role="group" aria-labelledby={`orgr-course-${index}`}>
          <button type="button" aria-pressed={mode === 'draw'} onClick={() => setMode('draw')}>
            <i className="bi bi-bezier2" aria-hidden="true" /> Draw on map
          </button>
          <button type="button" aria-pressed={mode === 'upload'} onClick={() => setMode('upload')}>
            <i className="bi bi-upload" aria-hidden="true" /> Upload GPX
          </button>
        </div>
      )}

      {/* The map and its tools belong to "Draw on map" (and the read-only view).
          "Upload GPX" stays what the field always was: the file input and its
          help, nothing else — the file is still read in the background so the
          line, start, finish and distance are filled in. */}
      {(mode === 'draw' || readOnly) && (hasCourse ? (
        <div className="orgr-preview">
          <RouteDrawer points={points} onChange={() => {}} fitKey={fitKey} height={240} readOnly scrollWheelZoom={false} />
          <span className="orgf-help">
            {totalDistanceKm(points).toFixed(2)} km course · {points.length} points
          </span>
        </div>
      ) : (
        <div className="orgr-empty">
          {readOnly ? 'No course was set.' : 'No course yet. Draw it on the map, one point at every turn.'}
        </div>
      ))}

      {!readOnly && mode === 'draw' && (
        <div className="orgr-actions">
          <button type="button" className="org-btn" onClick={() => setEditing(true)}>
            <i className="bi bi-pencil" aria-hidden="true" /> {hasCourse ? 'Edit course on map' : 'Draw course on map'}
          </button>
          {hasCourse && (
            <button type="button" className="org-btn ghost" onClick={() => downloadGpx(points, courseName)}>
              <i className="bi bi-download" aria-hidden="true" /> Download GPX
            </button>
          )}
          {(hasCourse || attached) && (
            <button type="button" className="orgf-remove" onClick={onRemove}>Remove course</button>
          )}
        </div>
      )}

      {!readOnly && mode === 'upload' && (
        <div className="orgr-upload">
          <FileUploadInput
            is_public
            value={route.gpx_file_url}
            onChange={onSavedFileChange}
            pendingFile={pendingFile}
            onPendingFile={(file) => onUploaded(file)}
            allowedFileTypes={['gpx']}
            inputProps={{ id: `orgf-gpx-${index}`, name: field }}
          />
        </div>
      )}

      {/*
        A LINK, not a button: on a finished event the whole activity body is a
        disabled <fieldset>, which disables every button inside it — the
        download included. A link is not a form control, so it stays usable.

      {readOnly && hasCourse && (
        <div className="orgr-actions">
          <button type="button" className="org-btn ghost" onClick={() => downloadGpx(points, courseName)}>
            <i className="bi bi-download" aria-hidden="true" /> Download GPX
          </button>
        </div>
      )}
      */}
      {readOnly && hasCourse && (
        <div className="orgr-actions">
          <GpxDownloadLink points={points} name={courseName} />
        </div>
      )}

      {/* The uploader shows its own file; this line is for the drawn course. */}
      {attached && mode === 'draw' && (
        <p className="orgr-attached">
          <i className="bi bi-paperclip" aria-hidden="true" /> GPX for participants:{' '}
          <span className="orgr-chip">{attached.name}</span>{' '}
          <span className="orgr-note">{attached.note}</span>
        </p>
      )}

      {message && (
        <p className={`orgr-status${message.tone === 'err' ? ' err' : ''}`} role="status">{message.text}</p>
      )}

      {mode === 'upload' && !readOnly ? (
        <span className="orgf-help">
          Optional. A GPX file is the course as GPS coordinates. Participants load it onto a
          Garmin, Coros or Strava so their watch can follow the route and warn them if they go
          off course. Export it from Strava, Garmin Connect or Plotaroute and upload it here. The
          event page map is drawn from it too.
        </span>
      ) : (
        <span className="orgf-help">
          The course is drawn on the event page and attached as a GPX file participants load onto a
          Garmin, Coros or Strava to follow it on the day. Drawing it here makes the file for you
          ({gpxFileName(courseName)}).
        </span>
      )}

      {editing && (
        <CourseEditorModal
          title={`Course · ${courseName}`}
          initialPoints={points}
          onCancel={closeEditor}
          onSave={onDrawn}
        />
      )}
    </div>
  );
}
